import { Button } from '@design-system';
import { releaseNameFor } from '@utils/product';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useState } from 'react';

import { ProductEditionSize } from '../../../../components/Content/ProductAbstract/ProductEditionSize';
import { Image } from '../../../../components/Image';
import { ProductPrice } from '../../../../components/ProductPrice/ProductPrice';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';
import { useDialogContext } from '../../../../hooks/useDialogContext';
import { useProductPurchasing } from '../../../../hooks/useProductPurchasing';
import { ProductGateway } from '../../../../types/AvantArteProduct';
import { GeneratedPrivateLink } from '../../../../types/generated';
import { GiftCardCheckoutPayload } from '../../../../types/GiftCard';
import {
  CheckoutGateway,
  ProductsGateway,
  ShopifyProductPurchaseDetails,
} from '../../../../types/Purchasing';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';
import { renderShopifyArtistName } from '../../../../utils/artists';
import { framedByDefault } from '../../../../utils/frames';
import { usePrivateRoomCheckoutLink } from '../../../checkout/hooks/usePrivateRoomCheckoutLink';
import { withFrameQuery } from '../../../checkout/utils/framingQuery';
import {
  selectedSetWorkIds,
  withWorksQuery,
} from '../../../checkout/utils/worksQuery';
import { EnquiryDrawer } from '../../../enquiry/components/EnquiryDrawer';
import { PrivateLinkProduct } from '../../types/PrivateLink';
import { productCTAState } from '../../utils';
import { ProductDetailsModal } from '../details/ProductDetailsModal';

const ProductFramingDrawer = dynamic(
  () =>
    import('../../../../components/Content/ProductAbstract/ProductFramingDrawer'),
  { ssr: false },
);

export type Props = {
  aaProductsGateway: ProductGateway;
  autoOpen?: boolean;
  checkoutGateway: CheckoutGateway<
    ShopifyProductPurchaseDetails,
    GiftCardCheckoutPayload
  >;
  privateLink: GeneratedPrivateLink;
  privateLinkProduct: PrivateLinkProduct;
  privateLinkSlug: string;
  productsGateway: ProductsGateway<
    ShopifyProductIdentifier,
    ShopifyProductPurchaseDetails
  >;
  showButtons: boolean;
  view: 'grid' | 'carousel';
  setShowDialog: () => void;
};

export const ProductSlide: React.FC<Props> = ({
  aaProductsGateway,
  autoOpen,
  checkoutGateway,
  privateLink,
  privateLinkProduct,
  privateLinkSlug,
  productsGateway,
  setShowDialog,
  showButtons = false,
  view,
}) => {
  const { t } = useTranslation('private');
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const { shopifyProduct, shopifyVariantId, storyblokProduct } =
    privateLinkProduct;
  const ctaState = productCTAState(privateLinkProduct);
  const frameIncluded = framedByDefault(shopifyProduct.framingOffered);
  const [analyticsEmitter] = useAnalyticsEmitter({
    'product_title': shopifyProduct.title,
    'product_artist': renderShopifyArtistName(shopifyProduct.artist),
    'product_framed_by_default': frameIncluded,
    'product_medium': shopifyProduct.medium,
    'product_price': shopifyProduct.price,
    'product_edition_size': shopifyProduct.editionSize,
    'shopify_product_id': shopifyProduct.id,
    'shopify_product_variant_id': privateLinkProduct.shopifyVariantId,
  });

  const [showProductDetails, setShowProductDetails] = useState(false);
  const { hideDialog, showDialog } = useDialogContext();
  const [showEnquiry, setShowEnquiry] = useState<boolean>(false);
  const [showFraming, setShowFraming] = useState(false);

  const goToCheckout = (link: string) => {
    hideDialog();
    setShowProductDetails(false);
    setShowFraming(false);
    void router.push(link);
  };

  const onBuyNowClick = () => {
    if (!checkoutLink) return;

    goToCheckout(
      withWorksQuery(
        checkoutLink,
        router.query.collectionSet
          ? selectedSetWorkIds(purchasingController.collectionSet)
          : [],
      ),
    );
  };

  const release = releaseNameFor(storyblokProduct as unknown as ProductType);

  const identifier = useMemo(() => {
    const id =
      storyblokProduct.component === 'product_v2'
        ? { id: storyblokProduct.shopifyId }
        : storyblokProduct.id[0];
    return id || { id: '' };
  }, [storyblokProduct]);

  const purchasingController = useProductPurchasing({
    identifier: identifier as { id: string },
    release,
    checkoutGateway,
    productsGateway,
    aaProductsGateway,
    variantIdentifier: shopifyVariantId,
    isPrivateSale: true,
    privateLinkIdentifier: privateLinkSlug,
    productDetails: null,
  });

  const checkoutLink = usePrivateRoomCheckoutLink({
    privateLink,
    privateLinkProduct,
  });

  const canBuyNow = Boolean(checkoutLink);

  useEffect(() => {
    if (autoOpen) setShowProductDetails(true);
  }, []);

  const productPrice = shopifyProduct.enquireOnlyNoPrice
    ? 'On Request'
    : Number(shopifyProduct.price);

  const onFrameDetailsClick = () => {
    setShowFraming(true);
  };

  const onShowProductDetails = () => void setShowProductDetails(true);

  // This logic is inside of a useEffect to ensure that the product state has been updated correctly
  // before showing the product details modal. For example, in the list view when the product name is clicked
  // the product state needs to be updated before the modal is shown.
  useEffect(() => {
    if (showProductDetails) {
      const frameIncluded = framedByDefault(
        purchasingController.product?.framingOffered,
      );

      showDialog(
        <ProductDetailsModal
          canBuyNow={canBuyNow}
          checkoutLoading={purchasingController.checkoutLoading}
          dispatchDate={purchasingController.dispatchDate}
          frame={purchasingController.frame}
          frameIncluded={frameIncluded}
          frames={purchasingController.frameOptions}
          framingOffered={
            purchasingController.product?.framingOffered || undefined
          }
          isFreeShipping={purchasingController.product?.freeShipping}
          onBuyNowClick={onBuyNowClick}
          onClose={() => {
            hideDialog();
            setShowProductDetails(false);
          }}
          onFrameDetailsClick={onFrameDetailsClick}
          privateLinkProduct={privateLinkProduct}
          purchasingController={purchasingController}
          setFrame={purchasingController.setFrame}
          setShowEnquiry={setShowEnquiry}
          tags={purchasingController.product?.tags}
        />,
        // We need to set dismissible to false to prevent user actions being blocked inside the lightbox
        { dismissible: false, verticalAlign: 'flex-end' },
      );
    }
  }, [
    canBuyNow,
    checkoutLink,
    showProductDetails,
    purchasingController.checkoutLoading,
    purchasingController.dispatchDate,
    purchasingController.frame,
  ]);

  return (
    <>
      <div
        className={classNames(
          'theme-light flex h-full w-full flex-col overflow-hidden rounded-lg bg-white',
          {
            'group': view === 'grid',
            'md:flex-row': view === 'carousel',
          },
        )}
      >
        {shopifyProduct.image ? (
          <button
            className="group w-full flex-1"
            onClick={() => void onShowProductDetails()}
            type="button"
          >
            <Image
              alt={shopifyProduct.title}
              hoverEffect
              size={{
                sm: {
                  ratio: '1/1',
                  span: 12,
                },
                md: {
                  ratio: view === 'grid' ? '1/1' : '3/4',
                  span: 12,
                },
              }}
              src={shopifyProduct.image}
            />
          </button>
        ) : null}

        <div
          className={classNames(
            { 'px-6 py-6': view === 'carousel' },
            'flex w-full flex-1 flex-col items-center justify-center gap-6 bg-white',
          )}
        >
          <button
            className={classNames(
              { 'max-w-sm': view === 'carousel' },
              { 'py-6': view === 'grid' },
              'flex w-full flex-col items-center gap-y-3 px-6 tabular-nums',
            )}
            onClick={() => void onShowProductDetails()}
            type="button"
          >
            <span className="flex w-full flex-col">
              {shopifyProduct.artist ? (
                <span className="font-bold">{shopifyProduct.artist}</span>
              ) : null}

              {shopifyProduct.title ? (
                <span className="line-clamp-2">{shopifyProduct.title}</span>
              ) : null}

              {productPrice ? (
                <span className="text-neutral-4">
                  <ProductPrice price={Number(productPrice)} />
                </span>
              ) : null}
            </span>

            {shopifyProduct.editionSize || 'editionNote' in storyblokProduct ? (
              <ProductEditionSize
                className="text-neutral-4"
                editionNote={
                  storyblokProduct.component === 'product'
                    ? storyblokProduct?.editionNote
                    : undefined
                }
                editionSize={shopifyProduct.editionSize}
              />
            ) : null}
          </button>

          {showButtons ? (
            <div className="flex w-full max-w-sm flex-row-reverse flex-col items-center gap-3">
              {ctaState === 'ENQUIRY' ? (
                <Button
                  fullWidth
                  label={t('slides.product.enquireNow')}
                  onClick={() => {
                    analyticsEmitter('enquire now', 'clicked', {
                      'product_can_be_framed': Boolean(
                        purchasingController.frameOptions?.length,
                      ),
                      'product_frame_upgrade_available': Boolean(
                        purchasingController.frameOptions,
                      ),
                      'product_framed_by_default': frameIncluded,
                    });
                    setShowEnquiry(true);
                  }}
                  size="lg"
                  type="button"
                  variant="primary"
                />
              ) : null}

              {ctaState === 'AVAILABLE' ? (
                <Button
                  disabled={!canBuyNow}
                  fullWidth
                  label={
                    breakpoint.name === 'sm'
                      ? t('slides.product.buyNow')
                      : t('slides.product.buyNowDesktop')
                  }
                  loading={purchasingController.checkoutLoading}
                  onClick={() => {
                    analyticsEmitter('buy now', 'clicked', {
                      'product_can_be_framed': Boolean(
                        purchasingController.frameOptions?.length,
                      ),
                      'product_frame_upgrade_available': Boolean(
                        purchasingController.frameOptions,
                      ),
                      'product_framed_by_default': frameIncluded,
                    });
                    setShowDialog();
                    onBuyNowClick();
                  }}
                  size="lg"
                  type="button"
                  variant="primary"
                />
              ) : null}

              {ctaState === 'SOLD_OUT' ? (
                <Button
                  disabled
                  fullWidth
                  label={t('slides.product.soldOut')}
                  size="lg"
                  type="button"
                  variant="primary"
                />
              ) : null}

              <Button
                fullWidth
                label={t('slides.product.seeMore')}
                onClick={onShowProductDetails}
                size="lg"
                tabIndex={0}
                type="button"
                variant="tertiary"
              />
            </div>
          ) : null}
        </div>
      </div>
      {purchasingController.product && (
        <ProductFramingDrawer
          onClose={() => {
            setShowFraming(false);
          }}
          onSubmit={
            checkoutLink
              ? (frame) => {
                  goToCheckout(withFrameQuery(checkoutLink, frame));
                }
              : undefined
          }
          overlayOtherDrawers
          product={storyblokProduct as unknown as ProductType}
          purchasingController={purchasingController}
          shown={showFraming}
        />
      )}

      <EnquiryDrawer
        onClose={() => setShowEnquiry(false)}
        product={privateLinkProduct.storyblokProduct}
        productPrice={Number(privateLinkProduct.shopifyProduct.price)}
        productPurchaseDetails={
          purchasingController && purchasingController.product
            ? purchasingController.product
            : undefined
        }
        productTitle={privateLinkProduct.shopifyProduct.title}
        shown={showEnquiry}
        source="PRIVATE_ROOM"
      />
    </>
  );
};
